<template>
  <div class="card executive-card mb-4" id="regional-map">
    <div class="card-header section-header border-0 pt-3 pb-0 px-3">
      <h5 class="section-title">Atuacao Regional Sul (PR, SC, RS)</h5>
      <span class="section-badge">Mapa de Cobertura Sul</span>
    </div>
    <div class="card-body pt-3">
      <div class="row">
        <div class="col-12 col-lg-6 mb-3 mb-lg-0">
          <div class="south-map">
            <div class="map-title">
              <strong>Mapa de Cobertura Sul</strong>
              <small>Bolhas proporcionais ao volume de receitas</small>
            </div>
            <div :id="mapId" class="leaflet-map"></div>
          </div>
        </div>

        <div class="col-12 col-lg-6">
          <div class="table-responsive">
            <table class="table table-sm align-middle mb-0 regional-table">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Receitas</th>
                  <th>Produtores</th>
                  <th>Cultivares</th>
                  <th>Tecnicos Ativos</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in tableRows" :key="row.state">
                  <td>
                    <span class="state-pill">{{ row.state }}</span>
                  </td>
                  <td>{{ row.receitas }}</td>
                  <td>{{ row.produtores }}</td>
                  <td>{{ row.cultivares }}</td>
                  <td>{{ row.tecnicosAtivos }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet'

export default {
  name: 'ExecutiveRegionalSection',
  props: {
    tableRows: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      map: null,
      bubblesLayer: null,
      mapId: `south-coverage-map-${this._uid}`,
      stateCoordinates: {
        PR: [-24.8, -51.4],
        SC: [-27.3, -50.2],
        RS: [-30.1, -53.0],
      },
    }
  },
  computed: {
    maxReceitas() {
      return Math.max(...this.tableRows.map((item) => item.receitas || 0), 1)
    },
  },
  mounted() {
    this.initMap()
    this.renderCoverage()
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  watch: {
    tableRows: {
      deep: true,
      handler() {
        this.renderCoverage()
      },
    },
  },
  methods: {
    initMap() {
      if (this.map) {
        return
      }

      this.map = L.map(this.mapId, {
        zoomControl: true,
        attributionControl: true,
        scrollWheelZoom: false,
      }).setView([-27.9, -51.8], 5)

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(this.map)

      this.bubblesLayer = L.layerGroup().addTo(this.map)
    },
    bubbleColor(state) {
      if (state === 'PR') return '#188651'
      if (state === 'SC') return '#0f5f7f'
      return '#1f3e63'
    },
    bubbleRadius(receitas) {
      const normalized = (receitas || 0) / this.maxReceitas
      return 18000 + normalized * 36000
    },
    renderCoverage() {
      if (!this.map || !this.bubblesLayer) {
        return
      }

      this.bubblesLayer.clearLayers()

      this.tableRows.forEach((row) => {
        const coords = this.stateCoordinates[row.state]
        if (!coords) {
          return
        }

        L.circle(coords, {
          radius: this.bubbleRadius(row.receitas),
          color: this.bubbleColor(row.state),
          weight: 2,
          fillColor: this.bubbleColor(row.state),
          fillOpacity: 0.28,
        })
          .bindPopup(
            `<strong>${row.state}</strong><br/>Receitas: ${row.receitas}<br/>Produtores: ${row.produtores}<br/>Cultivares: ${
              row.cultivares
            }<br/>Tecnicos Ativos: ${row.tecnicosAtivos}`
          )
          .addTo(this.bubblesLayer)

        L.marker(coords, {
          icon: L.divIcon({
            className: 'state-label-icon',
            html: `<div class="state-dot">${row.state}</div>`,
            iconSize: [44, 24],
            iconAnchor: [22, 12],
          }),
        }).addTo(this.bubblesLayer)
      })
    },
  },
}
</script>

<style scoped>
.executive-card {
  border: 1px solid #d8e5ec;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(9, 32, 58, 0.08);
}

.section-header {
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f2b40;
}

.section-badge {
  border-radius: 999px;
  border: 1px solid #c8dae8;
  background: #f0f7fb;
  padding: 4px 10px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: #2f5771;
}

.south-map {
  border-radius: 16px;
  background: radial-gradient(circle at 30% 0%, #edf8f3, #dde9f3 45%, #d2deea 100%);
  border: 1px solid #c9d9e5;
  padding: 12px;
}

.map-title {
  margin-bottom: 8px;
}

.map-title strong {
  display: block;
  font-size: 13px;
  color: #12364e;
}

.map-title small {
  color: #4d677c;
}

.leaflet-map {
  height: 340px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #c8d9e5;
}

:deep(.state-label-icon) {
  background: transparent;
  border: 0;
}

:deep(.state-dot) {
  background: rgba(16, 47, 69, 0.92);
  color: #fff;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  text-align: center;
}

th {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5f7383;
  border-top: 0;
}

tbody tr:nth-child(odd) {
  background: #f8fcff;
}

tbody tr:hover {
  background: #ecf5fb;
}

td {
  font-size: 13px;
  color: #1a364a;
  font-weight: 600;
  vertical-align: middle;
}

.state-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  padding: 3px 8px;
  border-radius: 999px;
  background: linear-gradient(120deg, #1a8e59, #0f617f);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.regional-table {
  border-radius: 12px;
  overflow: hidden;
}
</style>
